if (!customElements.get('rx-configurator')) {
  customElements.define('rx-configurator', class RxConfigurator extends HTMLElement {

    constructor() {
      super();
      this.currentStep = 1;
      this.sameForBothEyes = false;
      this.selections = {
        visionType: null,
        prescription: {
          od: { sph: '', cyl: '', axis: '' },
          os: { sph: '', cyl: '', axis: '' },
          pd: '',
          add: ''
        },
        lensPackage: null
      };
      this.productData = null;

      this.lensPackages = [
        { key: 'standard', name: 'Standard', description: 'Standard polarized lenses', surcharge: 0 },
        { key: 'premium', name: 'Premium', description: 'Premium multi-coated lenses with anti-scratch', surcharge: 2000 },
        { key: 'elite', name: 'Elite', description: 'Elite lenses with hydrophobic, oleophobic, and anti-reflective coatings', surcharge: 4000 }
      ];

      this.visionTypeLabels = {
        single_vision: 'Single Vision',
        progressive: 'Progressive',
        reading: 'Reading'
      };
    }

    /* ------------------------------------------------------------------
     *  Lifecycle
     * ----------------------------------------------------------------*/

    connectedCallback() {
      this.populateDropdowns();
      this.bindEvents();
      this.goToStep(1);
      this.loadProductData();
    }

    disconnectedCallback() {
      // Clean up bound listeners if needed
    }

    /* ------------------------------------------------------------------
     *  Product Data — fetched via Shopify AJAX API
     * ----------------------------------------------------------------*/

    async loadProductData() {
      const params = new URLSearchParams(window.location.search);
      const handle = params.get('product');
      const variantParam = params.get('variant');

      if (!handle) {
        console.warn('[rx-configurator] No product handle in URL params');
        return;
      }

      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}.js`);
        if (!res.ok) throw new Error(`Product not found: ${res.status}`);
        const product = await res.json();

        // Find selected variant
        let variant = product.variants[0];
        if (variantParam) {
          const vid = parseInt(variantParam, 10);
          const found = product.variants.find(v => v.id === vid);
          if (found) variant = found;
        }

        // Read surcharges from data attributes
        const sv = parseInt(this.dataset.surchargeSingleVision, 10) || 3000;
        const prog = parseInt(this.dataset.surchargeProgressive, 10) || 8000;
        const reading = parseInt(this.dataset.surchargeReading, 10) || 2000;

        this.productData = {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: `/products/${product.handle}`,
          price: variant.price,
          compare_at_price: variant.compare_at_price,
          image: variant.featured_image?.src || product.featured_image,
          variant_id: variant.id,
          variant_title: variant.title,
          surcharges: {
            single_vision: sv,
            progressive: prog,
            reading: reading
          }
        };

        this.updateProductUI();
      } catch (e) {
        console.error('[rx-configurator] Failed to load product:', e);
      }
    }

    updateProductUI() {
      if (!this.productData) return;

      // Update image
      const img = this.querySelector('#rx-product-image');
      if (img && this.productData.image) {
        img.src = this.productData.image;
        img.alt = this.productData.title;
        img.style.display = '';
      }

      // Update back link
      const backLink = this.querySelector('#rx-back-link');
      if (backLink) backLink.href = this.productData.url;
      const backLabel = this.querySelector('#rx-back-label');
      if (backLabel) backLabel.textContent = `Back to ${this.productData.title}`;

      // Update footer
      const footerName = this.querySelector('#rx-footer-name');
      if (footerName) footerName.textContent = this.productData.title;
      const footerVariant = this.querySelector('#rx-footer-variant');
      if (footerVariant) footerVariant.textContent = this.productData.variant_title;

      this.updateFooterPrice();
    }

    /* ------------------------------------------------------------------
     *  Dropdown Option Generation
     * ----------------------------------------------------------------*/

    populateDropdowns() {
      // SPH: -20.00 to +20.00, step 0.25
      const sphOptions = this.generateRangeOptions(-20, 20, 0.25, true);
      this.fillSelects('[data-rx-select="sph"]', sphOptions);

      // CYL: -6.00 to +6.00, step 0.25
      const cylOptions = this.generateRangeOptions(-6, 6, 0.25, true);
      this.fillSelects('[data-rx-select="cyl"]', cylOptions);

      // Axis: 1 to 180, integer
      const axisOptions = [];
      for (let i = 1; i <= 180; i++) {
        axisOptions.push({ value: String(i), label: String(i) });
      }
      this.fillSelects('[data-rx-select="axis"]', axisOptions);

      // PD: 50 to 76, integer
      const pdOptions = [];
      for (let i = 50; i <= 76; i++) {
        pdOptions.push({ value: String(i), label: String(i) });
      }
      this.fillSelects('[data-rx-select="pd"]', pdOptions);

      // ADD: +0.75 to +4.00, step 0.25
      const addOptions = this.generateRangeOptions(0.75, 4, 0.25, false);
      this.fillSelects('[data-rx-select="add"]', addOptions);
    }

    generateRangeOptions(min, max, step, includeSign) {
      const options = [];
      // Use integer arithmetic to avoid floating-point issues
      const minInt = Math.round(min / step);
      const maxInt = Math.round(max / step);

      for (let i = minInt; i <= maxInt; i++) {
        const val = i * step;
        const fixed = val.toFixed(2);
        let label;
        if (includeSign) {
          label = val > 0 ? `+${fixed}` : fixed;
        } else {
          label = val > 0 ? `+${fixed}` : fixed;
        }
        options.push({ value: fixed, label });
      }
      return options;
    }

    fillSelects(selector, options) {
      this.querySelectorAll(selector).forEach(select => {
        // Keep existing placeholder option if present
        const placeholder = select.querySelector('option[value=""]');
        select.innerHTML = '';
        if (placeholder) {
          select.appendChild(placeholder);
        } else {
          const opt = document.createElement('option');
          opt.value = '';
          opt.textContent = 'Select';
          opt.disabled = true;
          opt.selected = true;
          select.appendChild(opt);
        }
        options.forEach(({ value, label }) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = label;
          select.appendChild(opt);
        });
      });
    }

    /* ------------------------------------------------------------------
     *  Event Binding
     * ----------------------------------------------------------------*/

    bindEvents() {
      // Step 1 — Vision Type tiles
      this.querySelectorAll('.rx-tile[data-vision-type]').forEach(tile => {
        tile.addEventListener('click', () => this.selectVisionType(tile));
      });

      // Step 2 — Same-for-both checkbox
      const sameCheckbox = this.querySelector('#rx-same-eyes');
      if (sameCheckbox) {
        sameCheckbox.addEventListener('change', (e) => {
          this.sameForBothEyes = e.target.checked;
          if (this.sameForBothEyes) {
            this.mirrorOdToOs();
          }
          this.toggleOsFields();
          this.validatePrescription();
        });
      }

      // Step 2 — OD field changes (mirror to OS if checkbox active)
      ['sph', 'cyl', 'axis'].forEach(field => {
        const odSelect = this.querySelector(`[data-rx-select="${field}"][data-eye="od"]`);
        if (odSelect) {
          odSelect.addEventListener('change', () => {
            this.selections.prescription.od[field] = odSelect.value;
            if (this.sameForBothEyes) {
              this.mirrorOdToOs();
            }
            this.validatePrescription();
          });
        }

        const osSelect = this.querySelector(`[data-rx-select="${field}"][data-eye="os"]`);
        if (osSelect) {
          osSelect.addEventListener('change', () => {
            this.selections.prescription.os[field] = osSelect.value;
            this.validatePrescription();
          });
        }
      });

      // PD
      const pdSelect = this.querySelector('[data-rx-select="pd"]');
      if (pdSelect) {
        pdSelect.addEventListener('change', () => {
          this.selections.prescription.pd = pdSelect.value;
          this.validatePrescription();
        });
      }

      // ADD
      const addSelect = this.querySelector('[data-rx-select="add"]');
      if (addSelect) {
        addSelect.addEventListener('change', () => {
          this.selections.prescription.add = addSelect.value;
          this.validatePrescription();
        });
      }

      // Step 3 — Lens Package tiles
      this.querySelectorAll('.rx-tile[data-lens-package]').forEach(tile => {
        tile.addEventListener('click', () => this.selectLensPackage(tile));
      });

      // Navigation buttons
      this.querySelectorAll('.rx-back-btn').forEach(btn => {
        btn.addEventListener('click', () => this.prevStep());
      });

      this.querySelectorAll('.rx-continue-btn').forEach(btn => {
        btn.addEventListener('click', () => this.nextStep());
      });

      // Add to cart
      const addToCartBtn = this.querySelector('.rx-add-to-cart-btn');
      if (addToCartBtn) {
        addToCartBtn.addEventListener('click', () => this.addToCart());
      }
    }

    /* ------------------------------------------------------------------
     *  Step 1 — Vision Type
     * ----------------------------------------------------------------*/

    selectVisionType(tile) {
      const type = tile.dataset.visionType;
      if (!type) return;

      this.selections.visionType = type;

      this.querySelectorAll('.rx-tile[data-vision-type]').forEach(t => {
        t.classList.toggle('rx-tile--selected', t === tile);
      });

      this.updateAddFieldVisibility();
      this.updateFooterPrice();

      // Auto-advance after short delay
      setTimeout(() => this.goToStep(2), 300);
    }

    /* ------------------------------------------------------------------
     *  Step 2 — Prescription
     * ----------------------------------------------------------------*/

    mirrorOdToOs() {
      ['sph', 'cyl', 'axis'].forEach(field => {
        const odVal = this.selections.prescription.od[field];
        this.selections.prescription.os[field] = odVal;

        const osSelect = this.querySelector(`[data-rx-select="${field}"][data-eye="os"]`);
        if (osSelect) {
          osSelect.value = odVal;
        }
      });
    }

    toggleOsFields() {
      const osFieldset = this.querySelector('[data-rx-os-fields]');
      if (osFieldset) {
        osFieldset.classList.toggle('rx-fields--mirrored', this.sameForBothEyes);
        osFieldset.querySelectorAll('select').forEach(sel => {
          sel.disabled = this.sameForBothEyes;
        });
      }
    }

    updateAddFieldVisibility() {
      const addGroup = this.querySelector('[data-rx-add-group]');
      if (!addGroup) return;

      const needsAdd = this.selections.visionType === 'progressive' || this.selections.visionType === 'reading';
      addGroup.classList.toggle('rx-hidden', !needsAdd);

      if (!needsAdd) {
        this.selections.prescription.add = '';
        const addSelect = this.querySelector('[data-rx-select="add"]');
        if (addSelect) addSelect.value = '';
      }
    }

    validatePrescription() {
      const { od, os, pd, add } = this.selections.prescription;
      let valid = true;

      // SPH required for both eyes
      if (!od.sph || !os.sph) valid = false;

      // If CYL is selected and not 0.00, Axis must be filled (1-180)
      if (od.cyl && od.cyl !== '0.00') {
        const axisOd = parseInt(od.axis, 10);
        if (!od.axis || isNaN(axisOd) || axisOd < 1 || axisOd > 180) valid = false;
      }
      if (os.cyl && os.cyl !== '0.00') {
        const axisOs = parseInt(os.axis, 10);
        if (!os.axis || isNaN(axisOs) || axisOs < 1 || axisOs > 180) valid = false;
      }

      // PD required
      if (!pd) valid = false;

      // ADD required for progressive/reading
      const needsAdd = this.selections.visionType === 'progressive' || this.selections.visionType === 'reading';
      if (needsAdd && !add) valid = false;

      const continueBtn = this.querySelector('[data-step="2"] .rx-continue-btn');
      if (continueBtn) {
        continueBtn.disabled = !valid;
        continueBtn.classList.toggle('rx-btn--disabled', !valid);
      }

      return valid;
    }

    /* ------------------------------------------------------------------
     *  Step 3 — Lens Package
     * ----------------------------------------------------------------*/

    selectLensPackage(tile) {
      const key = tile.dataset.lensPackage;
      const pkg = this.lensPackages.find(p => p.key === key);
      if (!pkg) return;

      this.selections.lensPackage = pkg;

      this.querySelectorAll('.rx-tile[data-lens-package]').forEach(t => {
        t.classList.toggle('rx-tile--selected', t === tile);
      });

      this.updateFooterPrice();

      // Auto-advance after short delay
      setTimeout(() => this.goToStep(4), 300);
    }

    /* ------------------------------------------------------------------
     *  Step 4 — Review
     * ----------------------------------------------------------------*/

    renderReview() {
      const container = this.querySelector('[data-rx-review]');
      if (!container) return;

      const { visionType, prescription, lensPackage } = this.selections;
      const framePrice = this.productData?.price || 0;
      const visionSurcharge = this.getVisionSurcharge();
      const lensSurcharge = lensPackage?.surcharge || 0;
      const total = framePrice + visionSurcharge + lensSurcharge;

      const fmt = this.formatPrice;

      container.innerHTML = `
        <div class="rx-review__section">
          <h3 class="rx-review__heading">Frame</h3>
          <div class="rx-review__row">
            <span class="rx-review__label">${this.escapeHtml(this.productData?.title || 'Frame')}</span>
            <span class="rx-review__value">${fmt(framePrice)}</span>
          </div>
        </div>

        <div class="rx-review__section">
          <h3 class="rx-review__heading">Vision Type</h3>
          <div class="rx-review__row">
            <span class="rx-review__label">${this.escapeHtml(this.visionTypeLabels[visionType] || visionType)}</span>
            <span class="rx-review__value">${visionSurcharge > 0 ? '+' + fmt(visionSurcharge) : 'Included'}</span>
          </div>
        </div>

        <div class="rx-review__section">
          <h3 class="rx-review__heading">Prescription</h3>
          <table class="rx-review__table">
            <thead>
              <tr><th></th><th>SPH</th><th>CYL</th><th>Axis</th></tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>OD (Right)</strong></td>
                <td>${this.escapeHtml(prescription.od.sph || '-')}</td>
                <td>${this.escapeHtml(prescription.od.cyl || '-')}</td>
                <td>${this.escapeHtml(prescription.od.axis || '-')}</td>
              </tr>
              <tr>
                <td><strong>OS (Left)</strong></td>
                <td>${this.escapeHtml(prescription.os.sph || '-')}</td>
                <td>${this.escapeHtml(prescription.os.cyl || '-')}</td>
                <td>${this.escapeHtml(prescription.os.axis || '-')}</td>
              </tr>
            </tbody>
          </table>
          <div class="rx-review__row">
            <span class="rx-review__label">PD</span>
            <span class="rx-review__value">${this.escapeHtml(prescription.pd || '-')}</span>
          </div>
          ${prescription.add ? `
          <div class="rx-review__row">
            <span class="rx-review__label">ADD Power</span>
            <span class="rx-review__value">${this.escapeHtml(prescription.add)}</span>
          </div>` : ''}
        </div>

        <div class="rx-review__section">
          <h3 class="rx-review__heading">Lens Package</h3>
          <div class="rx-review__row">
            <span class="rx-review__label">${this.escapeHtml(lensPackage?.name || '-')}</span>
            <span class="rx-review__value">${lensSurcharge > 0 ? '+' + fmt(lensSurcharge) : 'Included'}</span>
          </div>
          <p class="rx-review__description">${this.escapeHtml(lensPackage?.description || '')}</p>
        </div>

        <div class="rx-review__section rx-review__total">
          <div class="rx-review__row">
            <span class="rx-review__label"><strong>Total</strong></span>
            <span class="rx-review__value"><strong>${fmt(total)}</strong></span>
          </div>
        </div>
      `;
    }

    /* ------------------------------------------------------------------
     *  Navigation
     * ----------------------------------------------------------------*/

    goToStep(n) {
      const step = Math.max(1, Math.min(4, n));
      this.currentStep = step;

      this.querySelectorAll('.rx-step').forEach(el => {
        el.classList.toggle('rx-step--active', parseInt(el.dataset.step, 10) === step);
      });

      this.updateProgress();

      // Step-specific setup
      if (step === 2) {
        this.updateAddFieldVisibility();
        this.validatePrescription();
      }
      if (step === 4) {
        this.renderReview();
      }

      this.updateFooterPrice();

      // Scroll to top of configurator
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    nextStep() {
      if (this.currentStep === 2 && !this.validatePrescription()) return;
      if (this.currentStep < 4) {
        this.goToStep(this.currentStep + 1);
      }
    }

    prevStep() {
      if (this.currentStep > 1) {
        this.goToStep(this.currentStep - 1);
      }
    }

    updateProgress() {
      this.querySelectorAll('.rx-progress__step').forEach(el => {
        const step = parseInt(el.dataset.step, 10);
        el.classList.toggle('rx-progress__step--completed', step < this.currentStep);
        el.classList.toggle('rx-progress__step--active', step === this.currentStep);
      });
    }

    /* ------------------------------------------------------------------
     *  Pricing
     * ----------------------------------------------------------------*/

    getVisionSurcharge() {
      if (!this.productData?.surcharges || !this.selections.visionType) return 0;
      return this.productData.surcharges[this.selections.visionType] || 0;
    }

    getTotalPrice() {
      const framePrice = this.productData?.price || 0;
      const visionSurcharge = this.getVisionSurcharge();
      const lensSurcharge = this.selections.lensPackage?.surcharge || 0;
      return framePrice + visionSurcharge + lensSurcharge;
    }

    formatPrice(cents) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(cents / 100);
    }

    updateFooterPrice() {
      const priceEl = this.querySelector('.rx-footer__price');
      if (priceEl) {
        priceEl.textContent = this.formatPrice(this.getTotalPrice());
      }
    }

    /* ------------------------------------------------------------------
     *  Cart Submission
     * ----------------------------------------------------------------*/

    async addToCart() {
      const btn = this.querySelector('.rx-add-to-cart-btn');
      if (!btn || btn.disabled) return;

      btn.disabled = true;
      btn.classList.add('rx-btn--loading');
      this.clearError();

      const { visionType, prescription, lensPackage } = this.selections;
      const visionLabel = this.visionTypeLabels[visionType] || visionType;
      const totalSurcharge = this.getVisionSurcharge() + (lensPackage?.surcharge || 0);

      const formData = new FormData();
      formData.append('id', this.productData.variant_id);
      formData.append('quantity', 1);
      formData.append('properties[Vision Type]', visionLabel);
      formData.append('properties[SPH OD]', prescription.od.sph);
      formData.append('properties[CYL OD]', prescription.od.cyl || '0.00');
      formData.append('properties[Axis OD]', prescription.od.axis || '-');
      formData.append('properties[SPH OS]', prescription.os.sph);
      formData.append('properties[CYL OS]', prescription.os.cyl || '0.00');
      formData.append('properties[Axis OS]', prescription.os.axis || '-');
      formData.append('properties[PD]', prescription.pd);

      if (prescription.add) {
        formData.append('properties[ADD Power]', prescription.add);
      }

      formData.append('properties[Lens Package]', lensPackage?.name || 'Standard');
      formData.append('properties[_rx_surcharge]', totalSurcharge);

      // Sections for cart drawer/notification update
      const cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        formData.append('sections', cartDrawer.getSectionsToRender().map(s => s.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const cartAddUrl = '/cart/add.js';
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        const data = await response.json();

        if (data.status) {
          // Shopify returns { status: 422, description: "..." } on error
          this.showError(data.description || 'Could not add to cart. Please try again.');
          btn.disabled = false;
          btn.classList.remove('rx-btn--loading');
          return;
        }

        // Success
        if (cartDrawer) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        }

        const cartUrl = window.routes?.cart_url || '/cart';
        window.location.href = cartUrl;
      } catch (err) {
        console.error('[rx-configurator] Cart submission error:', err);
        this.showError('Something went wrong. Please try again.');
        btn.disabled = false;
        btn.classList.remove('rx-btn--loading');
      }
    }

    /* ------------------------------------------------------------------
     *  Error display
     * ----------------------------------------------------------------*/

    showError(message) {
      let errorEl = this.querySelector('.rx-error');
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'rx-error';
        errorEl.setAttribute('role', 'alert');
        const step4 = this.querySelector('[data-step="4"]');
        if (step4) {
          step4.prepend(errorEl);
        }
      }
      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    clearError() {
      const errorEl = this.querySelector('.rx-error');
      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }
    }

    /* ------------------------------------------------------------------
     *  Utilities
     * ----------------------------------------------------------------*/

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  });
}
